import { computed, reactive, ref } from "vue";
import { defineStore } from "pinia";
import { watchDebounced } from "@vueuse/core";
import useMyFetch from "@stores/useMyFetch";
import type { QualityCheckDetail, QualityCheckRow, QualityDecision, QualityDimension } from "@/types";
import { captureEvent } from "@/lib/analytics";

interface IndexResponse {
  survey_customers: QualityCheckRow[];
  page: number;
  per_page: number;
  total_count: number;
  total_pages: number;
}

export const PER_PAGE = 25;

export const useQualityChecksStore = defineStore("qualityChecks", () => {
  const surveyToken = ref("");
  const rows = ref<QualityCheckRow[]>([]);
  const currentPage = ref(1);
  const totalCount = ref(0);
  const totalPages = ref(0);
  const loading = ref(false);

  const searchQuery = ref("");
  const sortColumn = ref("");
  const sortDirection = ref<"asc" | "desc">("asc");
  const autoFilter = ref<string[]>([]);
  const manualFilter = ref<string[]>([]);
  const finalFilter = ref<string[]>([]);
  const scoreFilters = reactive<Record<QualityDimension, number[]>>({
    substance: [],
    consistency: [],
    authenticity: [],
    appropriateness: [],
  });

  const activeFiltersCount = computed(
    () =>
      autoFilter.value.length +
      manualFilter.value.length +
      finalFilter.value.length +
      Object.values(scoreFilters).reduce((sum, values) => sum + values.length, 0),
  );

  const buildQuery = (page: number): URLSearchParams => {
    const query = new URLSearchParams({ page: String(page), per_page: String(PER_PAGE) });
    if (searchQuery.value) query.set("search", searchQuery.value);
    if (sortColumn.value) {
      query.set("sort_column", sortColumn.value);
      query.set("sort_direction", sortDirection.value);
    }
    autoFilter.value.forEach((value) => query.append("quality_auto[]", value));
    manualFilter.value.forEach((value) => query.append("quality_manual[]", value));
    finalFilter.value.forEach((value) => query.append("quality_final[]", value));
    Object.entries(scoreFilters).forEach(([dimension, notes]) => {
      notes.forEach((note) => query.append(`scores[${dimension}][]`, String(note)));
    });
    return query;
  };

  async function fetch(token: string, page = 1) {
    surveyToken.value = token;
    loading.value = true;
    try {
      const { data, error } = await useMyFetch(`/surveys/${token}/quality_checks?${buildQuery(page)}`).json<IndexResponse>();
      if (error.value) throw error.value;
      if (data.value) {
        rows.value = data.value.survey_customers;
        currentPage.value = data.value.page;
        totalCount.value = data.value.total_count;
        totalPages.value = data.value.total_pages;
      }
    } finally {
      loading.value = false;
    }
  }

  async function fetchDetail(id: number): Promise<QualityCheckDetail | null> {
    const { data, error } = await useMyFetch(`/surveys/${surveyToken.value}/quality_checks/${id}`).json<QualityCheckDetail>();
    if (error.value) throw error.value;
    return data.value;
  }

  async function decide(ids: number[], decision: QualityDecision) {
    const { data, error } = await useMyFetch(`/surveys/${surveyToken.value}/quality_checks/decide`)
      .patch({ quality_check: { decision, survey_customer_ids: ids } })
      .json<{ survey_customers: QualityCheckRow[] }>();
    if (error.value) throw error.value;
    captureEvent("quality_check_decided", { decision, respondents_count: ids.length });

    const updated = new Map((data.value?.survey_customers ?? []).map((row) => [row.id, row]));
    rows.value = rows.value.map((row) => updated.get(row.id) ?? row);
    if (activeFiltersCount.value > 0) await fetch(surveyToken.value, currentPage.value);
  }

  function toggleSort(column: string) {
    if (sortColumn.value === column) sortDirection.value = sortDirection.value === "asc" ? "desc" : "asc";
    else {
      sortColumn.value = column;
      sortDirection.value = "asc";
    }
    return fetch(surveyToken.value, 1);
  }

  function reset() {
    surveyToken.value = "";
    searchQuery.value = "";
    sortColumn.value = "";
    sortDirection.value = "asc";
    autoFilter.value = [];
    manualFilter.value = [];
    finalFilter.value = [];
    Object.values(scoreFilters).forEach((values) => values.splice(0));
  }

  watchDebounced(
    [searchQuery, autoFilter, manualFilter, finalFilter, scoreFilters],
    () => surveyToken.value && fetch(surveyToken.value, 1),
    { debounce: 300, deep: true },
  );

  return {
    rows,
    currentPage,
    totalCount,
    totalPages,
    loading,
    searchQuery,
    sortColumn,
    sortDirection,
    autoFilter,
    manualFilter,
    finalFilter,
    scoreFilters,
    activeFiltersCount,
    fetch,
    fetchDetail,
    decide,
    toggleSort,
    reset,
  };
});
